import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface Props {
  label: string;
  tooltip: string;
  checked: boolean;
  disabled?: boolean;
  disabledHint?: string;
  /**
   * Showing a brand's value that the brand does not own — it is the account's, and the brand
   * will move with it. The switch position alone cannot say this, so an operator looking at a
   * brand cannot otherwise tell a deliberate choice from a default it is still following.
   */
  inherited?: boolean;
  onToggle: (next: boolean) => void;
}

export function FlagToggleCard({
  label,
  tooltip,
  checked,
  disabled,
  disabledHint,
  inherited,
  onToggle
}: Props) {
  return (
    <div className='flex items-center justify-between rounded-lg border p-4'>
      <div className='min-w-0 pr-4'>
        <div className='flex items-center gap-1.5'>
          <span className='text-sm font-medium'>{label}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className='h-3.5 w-3.5 text-muted-foreground' />
            </TooltipTrigger>
            <TooltipContent className='max-w-xs'>{tooltip}</TooltipContent>
          </Tooltip>
          {inherited && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant='outline' className='shrink-0 text-[10px] font-normal'>
                  inherited
                </Badge>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                This brand has not set this switch. It shows the account&apos;s value and will
                change whenever the account&apos;s does. Toggling it here makes it the
                brand&apos;s own.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {disabled && disabledHint && (
          <p className='mt-1 text-xs text-muted-foreground'>{disabledHint}</p>
        )}
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onToggle}
      />
    </div>
  );
}
